import { useMemo } from 'react';

import useSpecialMenuCards from 'src/dispensary/hooks/use-special-menu-cards';
import { SpecialMenuCard } from 'src/gql/graphql';

import {
  CHIP_LABELS,
  createSpecialMatcher,
  FILTERABLE_KEYS,
  getFilterLabel,
  isSpecialFilterKey,
  specialsMatchFilterKey,
  type SpecialFilterKey,
  toggleSpecialFilter,
} from 'utils/helpers/special-filter';
import useProductQueryParams from './use-product-query-params';

export {
  CHIP_LABELS,
  createSpecialMatcher,
  FILTERABLE_KEYS,
  getFilterLabel,
  isSpecialFilterKey,
  specialsMatchFilterKey,
  type SpecialFilterKey,
  toggleSpecialFilter,
};

type ProductFilterOverrides = {
  isOnSpecial: boolean;
  specialIds?: string[];
};

type UseSpecialFilterParamResult = {
  filterOverrides: ProductFilterOverrides | null;
  matchingSpecials: SpecialMenuCard[];
  activeFilterLabels: string[];
  specialFilter: string[];
  loading: boolean;
};

export function useSpecialFilterParam(): UseSpecialFilterParamResult {
  const {
    queryParams: { specialFilter },
  } = useProductQueryParams();

  const hasSpecificFilters = specialFilter.some((v) => isSpecialFilterKey(v) && v !== 'all');
  const { specials, loading } = useSpecialMenuCards({ skip: !hasSpecificFilters });

  const result = useMemo(() => {
    const activeKeys = specialFilter.filter(isSpecialFilterKey);

    if (activeKeys.length === 0) {
      return {
        filterOverrides: null,
        matchingSpecials: [] as SpecialMenuCard[],
        activeFilterLabels: [] as string[],
      };
    }

    const activeFilterLabels = activeKeys.map((k) => CHIP_LABELS[k].label);

    if (activeKeys.includes('all')) {
      return {
        filterOverrides: { isOnSpecial: true },
        matchingSpecials: [] as SpecialMenuCard[],
        activeFilterLabels,
      };
    }

    const matchers = activeKeys.map(createSpecialMatcher).filter(Boolean) as ((s: SpecialMenuCard) => boolean)[];
    const matched = specials.filter((s) => matchers.some((m) => m(s)));
    const matchedIds = matched.map((s) => s._id).filter(Boolean) as string[];

    return {
      filterOverrides: { isOnSpecial: true, specialIds: matchedIds },
      matchingSpecials: matched,
      activeFilterLabels,
    };
  }, [specialFilter, specials]);

  return { ...result, specialFilter, loading };
}
